'use client';

/**
 * Client-side marketing-attribution persistence. When a customer lands on the catalog via a
 * campaign link (`?utm_source=&utm_campaign=`), we persist the attribution per store so a later
 * checkout — possibly on another page or after navigating around — can still attribute the order
 * to the campaign. Mirrors the `linkTracking` welcome-link pattern.
 *
 * Last-touch attribution: a newer campaign link overwrites the stored one for that store.
 * Affiliate short codes (`utm_affiliate`) are stored alongside for checkout split attribution.
 */

import { normalizeAffiliateShortCode } from '@/lib/affiliateLinkCode';
import { normalizeUtmCampaign, normalizeUtmSource } from '@/lib/utmSources';

const ATTRIBUTION_STORAGE_KEY = 'vupt-attribution-v1';

const isBrowser = typeof window !== 'undefined';

export type StoreAttribution = {
  /** Always one of `UTM_SOURCES`, or null when the visit only carried an affiliate code. */
  utmSource: string | null;
  utmCampaign: string | null;
  /** Affiliate short code from `utm_affiliate` (checkout split attribution). */
  utmAffiliate?: string | null;
};

function readAll(): Record<string, StoreAttribution> {
  if (!isBrowser) return {};
  try {
    const raw = window.localStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, StoreAttribution>;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeAll(all: Record<string, StoreAttribution>): void {
  window.localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(all));
}

/** Persist the campaign attribution for a store (last-touch wins). No-op without a valid source. */
export function rememberStoreAttribution(
  storeId: string,
  rawUtmSource: string | null | undefined,
  rawUtmCampaign?: string | null
): void {
  if (!isBrowser || !storeId) return;
  const utmSource = normalizeUtmSource(rawUtmSource);
  if (!utmSource) return;
  const utmCampaign = normalizeUtmCampaign(rawUtmCampaign);
  try {
    const all = readAll();
    const previous = all[storeId];
    all[storeId] = {
      utmSource,
      utmCampaign,
      utmAffiliate: previous?.utmAffiliate ?? null,
    };
    writeAll(all);
  } catch {
    /* storage unavailable — attribution is best-effort */
  }
}

/**
 * Persist affiliate short code for a store (last-touch wins). Keeps any existing campaign
 * attribution when present; an affiliate link is not a campaign channel, so the source stays null
 * until a real `utm_source` arrives — writing a synthetic one would put a value outside
 * `UTM_SOURCES` in storage.
 */
export function rememberAffiliateAttribution(
  storeId: string,
  rawUtmAffiliate: string | null | undefined
): void {
  if (!isBrowser || !storeId) return;
  const utmAffiliate = normalizeAffiliateShortCode(rawUtmAffiliate);
  if (!utmAffiliate) return;
  try {
    const all = readAll();
    const previous = all[storeId];
    all[storeId] = {
      utmSource: previous?.utmSource ?? null,
      utmCampaign: previous?.utmCampaign ?? null,
      utmAffiliate,
    };
    writeAll(all);
  } catch {
    /* storage unavailable — attribution is best-effort */
  }
}

/** Read the persisted campaign attribution for a store, or null if the visit was organic. */
export function getStoreAttribution(storeId: string): StoreAttribution | null {
  if (!isBrowser || !storeId) return null;
  return readAll()[storeId] ?? null;
}
